import { ERRORS } from "../../../constants/errors";
import type { Answers, Group, Groups } from "../../../features/questionnaire";
import type { VisibilityMap } from "../../Questionnaire/validation/form/types";
import type { PartialQuestion } from "../../Questionnaire/validation/form/types";
import type { ValidationResponse } from "../../Questionnaire/validation/question/rules/types";

export const requiresChildSelection = (group: Group): boolean =>
  group.kind === "group" && group.defaultNoAnswers != null;

const isVisibleOnCurrentPage = (name: string, visibilityMap: VisibilityMap) =>
  visibilityMap?.[name] === true;

// An empty array means "user cleared selections" — treat as unanswered.
const isChildAnswered = (childName: string, answers: Answers): boolean => {
  const value = answers[childName];
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return value != null && value !== "";
};

const isChildAnsweredYes = (childName: string, answers: Answers): boolean => {
  const value = answers[childName];
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "boolean") {
    return value;
  }
  return value === "yes";
};

/** Returns a failing ValidationResponse if primary is "Yes" but no visible child is, or null. */
export const validateYesNoToggleGroup = ({
  question,
  groups,
  answers,
  visibilityMap,
}: {
  question: PartialQuestion;
  groups?: Groups;
  answers: Answers;
  visibilityMap?: VisibilityMap;
}): ValidationResponse | null => {
  const { name } = question;
  const group = groups?.[name];

  if (!group || !requiresChildSelection(group)) {
    return null;
  }

  if (answers[name] !== "yes") {
    return null;
  }

  const visibleChildren = group.names.filter(childName =>
    isVisibleOnCurrentPage(childName, visibilityMap),
  );

  // Defer validation until all visible children are answered to avoid premature errors.
  const allChildrenAnswered =
    visibleChildren.length > 0 &&
    visibleChildren.every(childName => isChildAnswered(childName, answers));

  if (!allChildrenAnswered) {
    return null;
  }

  const hasChildAnsweredYes = visibleChildren.some(childName =>
    isChildAnsweredYes(childName, answers),
  );

  if (hasChildAnsweredYes) {
    return null;
  }

  return {
    id: name,
    valid: false,
    message: ERRORS.YES_NO_TOGGLE_GROUP_NO_YES_CHILD,
  };
};
